import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsApi, cartApi } from '../utils/api';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [cartLoading, setCartLoading] = useState(false);

  useEffect(() => {
    fetchProductDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await productsApi.getProductDetail(id);
      
      if (response && response.data) {
        setProduct(response.data);
      } else {
        setError('商品不存在');
      }
    } catch (err) {
      console.error('Error fetching product detail:', err);
      setError(err.message || '获取商品详情失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      setCartLoading(true);
      await cartApi.addToCart(id, quantity);
      alert(`已添加 ${quantity} 件商品到购物车`);
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert('添加到购物车失败，请稍后重试');
    } finally {
      setCartLoading(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= (product?.stock || 99)) {
      setQuantity(newQuantity);
    }
  };

  const formatPrice = (price) => {
    return typeof price === 'number' ? price.toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-container">
        <div className="error-message">
          <h2>😕 加载失败</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/shop')} className="back-button">
            返回商店
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-container">
        <div className="error-message">
          <h2>商品不存在</h2>
          <button onClick={() => navigate('/shop')} className="back-button">
            返回商店
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-container">
      {/* 面包屑导航 */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/')} className="breadcrumb-link">首页</button>
        <span className="breadcrumb-separator">></span>
        <button onClick={() => navigate('/shop')} className="breadcrumb-link">园艺店铺</button>
        <span className="breadcrumb-separator">></span>
        <span className="breadcrumb-current">{product.name}</span>
      </nav>

      <div className="product-detail-content">
        {/* 商品图片区域 */}
        <div className="product-images">
          <div className="main-image">
            <img 
              src={product.images?.[selectedImage]?.url || product.mainImage || product.image} 
              alt={product.name}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1556227834-09f1de7a7d14?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
              }}
            />
          </div>
          
          {product.images && product.images.length > 1 && (
            <div className="image-thumbnails">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                  onClick={() => setSelectedImage(index)}
                >
                  <img src={image.url} alt={`${product.name} 图片 ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 商品信息区域 */}
        <div className="product-info">
          <div className="product-header">
            <h1 className="product-title">{product.name}</h1>
            
            {/* 商品标签 */}
            <div className="product-badges">
              {product.isNew && <span className="badge new">新品</span>}
              {product.isBestseller && <span className="badge bestseller">热销</span>}
              {product.isFeatured && <span className="badge featured">推荐</span>}
              {product.stock && product.stock <= 5 && (
                <span className="badge low-stock">库存紧张</span>
              )}
            </div>
          </div>

          {/* 价格信息 */}
          <div className="price-section">
            <div className="current-price">¥{formatPrice(product.price)}</div>
            {product.memberPrice && product.memberPrice < product.price && (
              <div className="member-price">会员价: ¥{formatPrice(product.memberPrice)}</div>
            )}
            {product.originalPrice && product.originalPrice > product.price && (
              <div className="original-price">原价: ¥{formatPrice(product.originalPrice)}</div>
            )}
          </div>

          {/* 商品描述 */}
          <div className="product-description">
            <p>{product.description || product.shortDescription}</p>
          </div>

          {/* 商品特点 */}
          {product.features && product.features.length > 0 && (
            <div className="product-features">
              <h3>商品特点</h3>
              <ul>
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 库存状态 */}
          <div className="stock-status">
            <span className="stock-label">库存状态: </span>
            {product.stock > 0 ? (
              <span className="in-stock">有库存 ({product.stock} 件)</span>
            ) : (
              <span className="out-of-stock">暂时缺货</span>
            )}
          </div>

          {/* 购买操作区域 */}
          <div className="purchase-section">
            <div className="quantity-selector">
              <span className="quantity-label">数量:</span>
              <div className="quantity-controls">
                <button 
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button 
                  className="quantity-btn"
                  onClick={() => handleQuantityChange(1)}
                  disabled={quantity >= (product.stock || 99)}
                >
                  +
                </button>
              </div>
            </div>

            <div className="action-buttons">
              <button 
                className="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={product.stock <= 0 || cartLoading}
              >
                {cartLoading ? '添加中...' : '加入购物车'}
              </button>
              
              <button 
                className="buy-now-btn"
                onClick={() => {
                  // 立即购买逻辑
                  handleAddToCart();
                  navigate('/checkout');
                }}
                disabled={product.stock <= 0}
              >
                立即购买
              </button>
            </div>
          </div>

          {/* 其他信息 */}
          <div className="product-meta">
            {product.sku && (
              <div className="meta-item">
                <span className="meta-label">商品编号:</span>
                <span className="meta-value">{product.sku}</span>
              </div>
            )}
            {product.category && (
              <div className="meta-item">
                <span className="meta-label">商品分类:</span>
                <span className="meta-value">{product.category.name || product.category}</span>
              </div>
            )}
            {product.weight && (
              <div className="meta-item">
                <span className="meta-label">重量:</span>
                <span className="meta-value">{product.weight}kg</span>
              </div>
            )}
            {product.dimensions && (
              <div className="meta-item">
                <span className="meta-label">尺寸:</span>
                <span className="meta-value">{product.dimensions}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 商品详细描述 */}
      {product.detailDescription && (
        <div className="product-detail-description">
          <h2>商品详情</h2>
          <div className="detail-content">
            {typeof product.detailDescription === 'string' ? (
              <div dangerouslySetInnerHTML={{ __html: product.detailDescription }} />
            ) : (
              <p>{product.detailDescription}</p>
            )}
          </div>
        </div>
      )}

      {/* 相关商品推荐 */}
      <div className="related-products">
        <h2>相关推荐</h2>
        <p>这里可以展示相关商品...</p>
      </div>
    </div>
  );
}

export default ProductDetail;
